import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as uiSlice from '../../../data/ui.slice';
import * as userSlice from '../../../data/user.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as driverSlice from '../../../data/driver.slice';
import accountingService from '../../../services/accountingService';
import { ProviderInvoiceLineItemReviewLog } from '../../../data/models/provider-invoice-line-item.model';
import {
  Dialog,
  DialogContent,
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Slide,
  Chip,
  MenuItem,
  TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import { DateTime } from 'luxon';
import TimeInput from '../../forms/TimeInput';

const PREFIX = 'ReviewInvoiceDialog';

const classes = {
  dialog: `${PREFIX}-dialog`,
  dialogPaper: `${PREFIX}-dialogPaper`,
  header: `${PREFIX}-header`,
  headerWrapper: `${PREFIX}-headerWrapper`,
  headerContent: `${PREFIX}-headerContent`,
  tripCode: `${PREFIX}-tripCode`,
  title: `${PREFIX}-title`,
  closeButton: `${PREFIX}-closeButton`,
  content: `${PREFIX}-content`,
  leftCard: `${PREFIX}-leftCard`,
  rightCard: `${PREFIX}-rightCard`,
  providerHeader: `${PREFIX}-providerHeader`,
  providerName: `${PREFIX}-providerName`,
  providerBadge: `${PREFIX}-providerBadge`,
  providerInfo: `${PREFIX}-providerInfo`,
  driverName: `${PREFIX}-driverName`,
  phoneNumber: `${PREFIX}-phoneNumber`,
  iconButton: `${PREFIX}-iconButton`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  sectionContent: `${PREFIX}-sectionContent`,
  summaryTitle: `${PREFIX}-summaryTitle`,
  summaryText: `${PREFIX}-summaryText`,
  buttonSet: `${PREFIX}-buttonSet`,
  nextButton: `${PREFIX}-nextButton`,
  closeButtonAction: `${PREFIX}-closeButtonAction`,
  dialogContent: `${PREFIX}-dialogContent`,
  formControl: `${PREFIX}-formControl`,
  radioLabel: `${PREFIX}-radioLabel`,
  approveBox: `${PREFIX}-approveBox`,
  selectField: `${PREFIX}-selectField`,
  adjustBox: `${PREFIX}-adjustBox`,
  timePickerField: `${PREFIX}-timePickerField`,
  summaryContent: `${PREFIX}-summaryContent`,
  textFieldLabel: `${PREFIX}-textFieldLabel`,
  textFieldInput: `${PREFIX}-textFieldInput`,
  selectInput: `${PREFIX}-selectInput`,
  timePickerInput: `${PREFIX}-timePickerInput`,
  pickerIcon: `${PREFIX}-pickerIcon`,
  highlighted: `${PREFIX}-highlighted`,
  highlightedProvider: `${PREFIX}-highlightedProvider`,
  highlightedTime: `${PREFIX}-highlightedTime`,
  highlightedLabel: `${PREFIX}-highlightedLabel`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogPaper}`]: {
    backgroundColor: theme.palette.secondary.main,
    maxWidth: '100vw',
    width: '100vw',
    margin: 0,
    height: '100vh',
    maxHeight: '100vh',
  },
  [`& .${classes.header}`]: {
    backgroundImage: `linear-gradient(90deg, ${theme.palette.lightActionDisabled04p.main} 0%, ${theme.palette.lightActionDisabled04p.main} 100%)`,
    padding: `${theme.spacing(6)} 0 ${theme.spacing(3)}`,
    position: 'relative',
  },
  [`& .${classes.headerWrapper}`]: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
    padding: `0 ${theme.spacing(18)}`,
  },
  [`& .${classes.headerContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexGrow: 1,
  },
  [`& .${classes.tripCode}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '22px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: theme.palette.lightActionDisabled90p.main,
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: '36px',
    fontWeight: 700,
    lineHeight: '48px',
    letterSpacing: '0.5px',
    color: theme.palette.lightTextPrimary.main,
  },
  [`& .${classes.closeButton}`]: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    gap: theme.spacing(3),
    padding: `${theme.spacing(3)} ${theme.spacing(18)} ${theme.spacing(12.5)}`,
  },
  [`& .${classes.leftCard}`]: {
    flex: '1 1 50%',
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '3px',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    padding: `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(5)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    alignSelf: 'flex-start',
  },
  [`& .${classes.rightCard}`]: {
    flex: '1 1 50%',
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '3px',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignSelf: 'flex-start',
  },
  [`& .${classes.providerHeader}`]: {
    display: 'flex',
    gap: theme.spacing(6),
    alignItems: 'center',
  },
  [`& .${classes.providerName}`]: {
    fontFamily: 'Bitter',
    fontSize: '21px',
    fontWeight: 700,
    lineHeight: '32px',
    color: theme.palette.lightTextPrimary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  [`& .${classes.providerBadge}`]: {
    height: '16px',
    minWidth: '16px',
    '& .MuiChip-label': {
      borderRadius: '50%',
      padding: '0 ' + theme.spacing(0.75),
      lineHeight: '24px',
      color: theme.palette.lightTextPrimary.main + ' !important',
      fontSize: '11px',
      fontWeight: 600,
    },
  },
  [`& .${classes.providerInfo}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.driverName}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: theme.palette.lightActionDisabled90p.main,
  },
  [`& .${classes.phoneNumber}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: theme.palette.lightTextSecondary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  [`& .${classes.iconButton}`]: {
    padding: theme.spacing(0.25),
    width: '24px',
    height: '24px',
    color: theme.palette.lightTextPrimary.main,
  },
  [`& .${classes.sectionTitle}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: theme.palette.lightActionDisabled90p.main,
    paddingBottom: theme.spacing(1),
  },
  [`& .${classes.sectionContent}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: theme.palette.lightActionDisabled90p.main,
  },
  [`& .${classes.summaryTitle}`]: {
    fontFamily: 'Bitter',
    fontSize: '21px',
    fontWeight: 700,
    lineHeight: '32px',
    color: theme.palette.lightTextPrimary.main,
  },
  [`& .${classes.summaryText}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: theme.palette.lightActionDisabled90p.main,
    '& strong': {
      fontWeight: 600,
    },
  },
  [`& .${classes.buttonSet}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  [`& .${classes.nextButton}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    fontFamily: 'Source Sans Pro',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.46px',
    padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
    minWidth: '144px',
    borderRadius: '3px',
    boxShadow: `0px 2px 7px 0px ${theme.palette.darkShadow30p.main}`,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.buttonHoverColor.main,
    },
  },
  [`& .${classes.closeButtonAction}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.main,
    fontFamily: 'Source Sans Pro',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.46px',
    padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
    minWidth: '144px',
    borderRadius: '3px',
    border: `1px solid ${theme.palette.primary.main}`,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.lightActionDisabled04p.main,
    },
  },
  [`& .${classes.dialogContent}`]: {
    padding: 0,
  },
  [`& .${classes.formControl}`]: {
    width: '100%',
  },
  [`& .${classes.radioLabel}`]: {
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Source Sans Pro',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
  },
  [`& .${classes.approveBox}`]: {
    paddingLeft: theme.spacing(5),
    paddingBottom: theme.spacing(2),
  },
  [`& .${classes.selectField}`]: {
    width: '337px',
  },
  [`& .${classes.adjustBox}`]: {
    paddingLeft: theme.spacing(5),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.timePickerField}`]: {
    width: '155px',
  },
  [`& .${classes.summaryContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  [`& .${classes.textFieldLabel}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.lightTextSecondary.main,
    transform: 'translate(12px, 6px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(12px, 6px) scale(1)',
    },
  },
  [`& .${classes.textFieldInput}`]: {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '3px',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontWeight: 600,
    paddingTop: '22px',
    paddingBottom: '4px',
  },
  [`& .${classes.selectInput}`]: {
    '& .MuiSelect-select': {
      paddingLeft: '12px',
      paddingRight: '32px',
      paddingTop: 0,
      paddingBottom: 0,
      lineHeight: '24px',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.lightTextSecondary.main,
    },
  },
  [`& .${classes.timePickerInput}`]: {
    height: '48px',
    paddingTop: '18px',
    paddingBottom: '0px',
    paddingLeft: '12px',
    paddingRight: '12px',
    '& input': {
      padding: 0,
      paddingTop: '4px',
      height: 'auto',
    },
    '&:before, &:after': {
      display: 'none',
    },
  },
  [`& .${classes.pickerIcon}`]: {
    color: theme.palette.lightTextSecondary.main,
  },
  [`& .${classes.highlighted}`]: {
    color: theme.palette.error.main + ' !important',
    fontWeight: 600,
  },
  [`& .${classes.highlightedProvider}`]: {
    color: theme.palette.error.main + ' !important',
  },
  [`& .${classes.highlightedTime}`]: {
    '& .MuiInputBase-root': {
      boxShadow: `0px 2px 7px 0px ${theme.palette.error.main}`,
    },
  },
  [`& .${classes.highlightedLabel}`]: {
    color: theme.palette.error.main + ' !important',
  },
}));

const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const reviewInvoiceSchema = z.object({
  outcome: z.string().optional(),
  approveReasonBC: z.string().optional(),
  adjustDriver: z.number(),
  onSiteTime: z.any(),
  onBoardTime: z.any(),
  completedTime: z.any(),
});

type ReviewInvoiceFormValues = z.infer<typeof reviewInvoiceSchema>;

const toDateTimeOnDate = (timeValue?: string, baseDateISO?: string) => {
  if (!timeValue || !baseDateISO) {
    return null;
  }
  const baseDate = DateTime.fromISO(baseDateISO).setZone('utc');
  if (!baseDate.isValid) {
    return null;
  }
  const parsedTime = DateTime.fromFormat(timeValue, 'h:mma');
  if (!parsedTime.isValid) {
    return null;
  }
  return baseDate.set({
    hour: parsedTime.hour,
    minute: parsedTime.minute,
    second: 0,
    millisecond: 0,
  });
};

interface ReviewInvoiceDialogProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  providerInvoiceLineItemID: number;
  providerInvoiceID: number;
  invoiceData: {
    tripCode: string;
    provider: string;
    providerBadge?: number;
    driverName: string;
    driverID?: number;
    phoneNumber?: string;
    reason: string;
    tripId: string;
    memberName: string;
    tripMode: string;
    pickupTime: string;
    pickupDate?: string;
    pickUpAddressTimeZoneIANA?: string;
    providerId?: number;
    pickupLocation: string;
    dropoffLocation: string;
    distance: string;
    price: string;
    onSite?: string;
    onBoard?: string;
    completed?: string;
    appointmentDate?: string;
    reviewLogs?: ProviderInvoiceLineItemReviewLog[];
  };
}

export default function ReviewInvoiceDialog({
  open,
  onClose,
  providerInvoiceLineItemID,
  providerInvoiceID,
  invoiceData,
}: ReviewInvoiceDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSlice.selectLoggedInUserInfo);
  const reviewReasons = useAppSelector(
    lookupSlice.selectProviderInvoiceLineItemReviewReasons,
  );
  const drivers = useAppSelector((state) =>
    invoiceData.providerId
      ? driverSlice.selectDriversByProvider(state, invoiceData.providerId)
      : [],
  );

  // Determine which fields should be highlighted based on review reason BCs
  const highlightFlags = useMemo(() => {
    const reasonBCs =
      invoiceData.reviewLogs?.map((log) => log.reviewReasonBC) || [];
    return {
      highlightReason: false,
      highlightPrice:
        reasonBCs.includes('INVOICEREVIEWAMOUNT') ||
        reasonBCs.includes('INVOICEREVIEWAMOUNTTHRESHOLD'),
      highlightProvider:
        reasonBCs.includes('INVOICEREVIEWPROVIDERDAYS') ||
        reasonBCs.includes('INVOICEREVIEWPROVIDERPERCENT') ||
        reasonBCs.includes('NEWPROVIDER'),
      highlightCompletedTime:
        reasonBCs.includes('INVOICEREVIEWONBOARDCOMPLETED') ||
        reasonBCs.includes('INVOICEREVIEWONSITECOMPLETED') ||
        reasonBCs.includes('ONBOARDTOCOMPLETED') ||
        reasonBCs.includes('ONSITETOONBOARD'),
      // reasonBCs.includes('ONSITETOCOMPLETED'), // currently explicitly not handled (F1V2-2821)
    };
  }, [invoiceData.reviewLogs]);

  const defaultValues = useMemo<ReviewInvoiceFormValues>(
    () => ({
      outcome: '',
      approveReasonBC: '',
      adjustDriver:
        drivers.find((d) => invoiceData.driverID === d.driverID)?.driverID ?? 0,
      onSiteTime: toDateTimeOnDate(
        invoiceData.onSite,
        invoiceData.appointmentDate,
      ),
      onBoardTime: toDateTimeOnDate(
        invoiceData.onBoard,
        invoiceData.appointmentDate,
      ),
      completedTime: toDateTimeOnDate(
        invoiceData.completed,
        invoiceData.appointmentDate,
      ),
    }),
    [
      invoiceData.onSite,
      invoiceData.onBoard,
      invoiceData.completed,
      invoiceData.pickupDate,
      invoiceData.appointmentDate,
    ],
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields },
  } = useForm<ReviewInvoiceFormValues>({
    resolver: zodResolver(reviewInvoiceSchema),
    defaultValues,
  });

  const shouldHighlightCompletedTimeInput =
    highlightFlags.highlightCompletedTime && !dirtyFields.completedTime;

  const outcome = watch('outcome');
  const approveReasonBC = watch('approveReasonBC');

  useEffect(() => {
    if (open) {
      dispatch(
        lookupSlice.fetchLookupItemsAsync(
          'providerInvoiceLineItemReviewReason',
        ),
      );
      if (invoiceData.providerId) {
        dispatch(
          driverSlice.fetchDriversByProviderAsync(invoiceData.providerId),
        );
      }
      if (user?.userAccountID) {
        dispatch(
          accountingSlice.assignProviderInvoiceAsync(
            providerInvoiceID,
            user.userAccountID,
          ),
        );
      }
    }
  }, [
    open,
    providerInvoiceID,
    user?.userAccountID,
    invoiceData.providerId,
    dispatch,
  ]);

  const previousOutcome = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (previousOutcome.current === outcome) {
      return;
    }

    if (outcome === 'approve') {
      if (!approveReasonBC && reviewReasons.length > 0) {
        setValue(
          'approveReasonBC',
          reviewReasons[0].providerInvoiceLineItemReviewReasonBC,
          { shouldDirty: false },
        );
      }
      setValue('adjustDriver', 0, { shouldDirty: false });
      setValue('onSiteTime', null, { shouldDirty: false });
      setValue('onBoardTime', null, { shouldDirty: false });
      setValue('completedTime', null, { shouldDirty: false });
    } else if (outcome === 'adjust') {
      setValue('approveReasonBC', '', { shouldDirty: false });
    }

    previousOutcome.current = outcome;
  }, [outcome, approveReasonBC, reviewReasons, setValue]);

  const handleClose = (shouldRefresh?: boolean) => {
    if (user?.userAccountID) {
      dispatch(
        accountingSlice.assignProviderInvoiceAsync(providerInvoiceID, null),
      );
    }
    onClose(shouldRefresh);
  };

  const handleNext = (values: ReviewInvoiceFormValues) => {
    if (values.outcome === 'approve' && values.approveReasonBC) {
      accountingService
        .approveReviewInvoice(
          providerInvoiceLineItemID,
          'APPROVE',
          values.approveReasonBC,
        )
        .subscribe({
          next: () => {
            dispatch(uiSlice.setSuccessMessage(t('invoice.tripApproved')));
            handleClose(true);
          },
          error: (err) => {
            dispatch(
              uiSlice.setErrorMessage(
                err.message || t('invoice.approvalFailed'),
              ),
            );
          },
        });
    } else if (values.outcome === 'adjust') {
      accountingService
        .adjustReviewInvoice(
          providerInvoiceLineItemID,
          values.adjustDriver,
          values.onSiteTime
            .setZone(invoiceData.pickUpAddressTimeZoneIANA, {
              keepLocalTime: true,
            })
            .toUTC()
            .toISO(),
          values.onBoardTime
            .setZone(invoiceData.pickUpAddressTimeZoneIANA, {
              keepLocalTime: true,
            })
            .toUTC()
            .toISO(),
          values.completedTime
            .setZone(invoiceData.pickUpAddressTimeZoneIANA, {
              keepLocalTime: true,
            })
            .toUTC()
            .toISO(),
        )
        .subscribe({
          next: () => {
            accountingService
              .approveReviewInvoice(
                providerInvoiceLineItemID,
                'APPROVE',
                reviewReasons[0].providerInvoiceLineItemReviewReasonBC,
              )
              .subscribe({
                next: () => {
                  dispatch(
                    uiSlice.setSuccessMessage(t('invoice.tripAdjusted')),
                  );
                  handleClose(true);
                },
                error: (err) => {
                  dispatch(
                    uiSlice.setErrorMessage(
                      err.message || t('invoice.approvalFailed'),
                    ),
                  );
                },
              });
          },
          error: (err) => {
            dispatch(
              uiSlice.setErrorMessage(
                err.message || t('invoice.approvalFailed'),
              ),
            );
          },
        });
      handleClose(true);
    } else {
      dispatch(uiSlice.setErrorMessage(t('invoice.pleaseSelectOutcome')));
    }
  };

  return (
    <StyledDialog
      fullScreen
      open={open}
      onClose={() => handleClose(false)}
      TransitionComponent={Transition}
      classes={{ paper: classes.dialogPaper }}
      className={classes.dialog}>
      <Box className={classes.header}>
        <IconButton
          className={classes.closeButton}
          onClick={() => handleClose(false)}
          aria-label="close"
          size="large">
          <CloseIcon />
        </IconButton>
        <Box className={classes.headerWrapper}>
          <Box className={classes.headerContent}>
            <Typography className={classes.tripCode}>
              {invoiceData.tripCode}
            </Typography>
            <Typography className={classes.title}>
              {t('invoice.reviewInvoicing')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.content}>
          <Box className={classes.leftCard}>
            <Box>
              <Box className={classes.providerHeader}>
                <Box>
                  <Typography className={`${classes.providerName}`}>
                    {invoiceData.provider}
                    {invoiceData.providerBadge && (
                      <Chip
                        label={invoiceData.providerBadge}
                        className={classes.providerBadge}
                      />
                    )}
                  </Typography>
                  <Box className={classes.providerInfo}>
                    <Typography className={classes.driverName}>
                      {invoiceData.driverName}
                    </Typography>
                    {invoiceData.phoneNumber && (
                      <Box className={classes.phoneNumber}>
                        {invoiceData.phoneNumber}
                        <IconButton className={classes.iconButton} size="small">
                          <ChatBubbleOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton className={classes.iconButton} size="small">
                          <CallOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography className={classes.sectionTitle}>
                {t('invoice.reasonForReview')}
              </Typography>
              <Typography
                className={`${classes.sectionContent} ${
                  highlightFlags.highlightReason ? classes.highlighted : ''
                }`}>
                {invoiceData.reason}
              </Typography>
            </Box>

            <Box>
              <Typography className={classes.sectionTitle}>
                {t('invoice.whatIsTheOutcome')}
              </Typography>
              <FormControl component="fieldset" className={classes.formControl}>
                <Controller
                  name="outcome"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value)}>
                      <FormControlLabel
                        value="approve"
                        control={<Radio />}
                        label={t('invoice.approve')}
                        className={classes.radioLabel}
                      />
                      {outcome === 'approve' && (
                        <Box className={classes.approveBox}>
                          <Controller
                            name="approveReasonBC"
                            control={control}
                            render={({ field: reasonField, fieldState }) => (
                              <TextField
                                select
                                label={t('invoice.reason')}
                                value={reasonField.value ?? ''}
                                onChange={reasonField.onChange}
                                variant="filled"
                                className={classes.selectField}
                                InputLabelProps={{
                                  shrink: true,
                                  className: classes.textFieldLabel,
                                }}
                                SelectProps={{
                                  displayEmpty: true,
                                }}
                                InputProps={{
                                  disableUnderline: true,
                                  className: `${classes.textFieldInput} ${classes.selectInput}`,
                                }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}>
                                {reviewReasons.map((reason) => (
                                  <MenuItem
                                    key={
                                      reason.providerInvoiceLineItemReviewReasonID
                                    }
                                    value={
                                      reason.providerInvoiceLineItemReviewReasonBC
                                    }>
                                    {
                                      reason.providerInvoiceLineItemReviewReasonName
                                    }
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          />
                        </Box>
                      )}
                      <FormControlLabel
                        value="adjust"
                        control={<Radio />}
                        label={t('invoice.adjust')}
                        className={classes.radioLabel}
                      />
                      {outcome === 'adjust' && (
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                          <Box className={classes.adjustBox}>
                            <Controller
                              name="adjustDriver"
                              control={control}
                              render={({ field: driverField, fieldState }) => (
                                <TextField
                                  select
                                  label={t('invoice.driver')}
                                  value={driverField.value ?? ''}
                                  onChange={driverField.onChange}
                                  variant="filled"
                                  className={classes.selectField}
                                  InputLabelProps={{
                                    shrink: true,
                                    className: classes.textFieldLabel,
                                  }}
                                  SelectProps={{
                                    displayEmpty: true,
                                  }}
                                  InputProps={{
                                    disableUnderline: true,
                                    className: `${classes.textFieldInput} ${classes.selectInput}`,
                                  }}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}>
                                  <MenuItem value="">Select driver</MenuItem>
                                  {drivers.map((driver) => (
                                    <MenuItem
                                      key={driver.driverID}
                                      value={driver.driverID}>
                                      {`${driver.driverFirstName || ''} ${driver.driverLastName || ''}`}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}
                            />

                            <TimeInput
                              name="onSiteTime"
                              label={t('invoice.onSite')}
                              control={control}
                              className={classes.timePickerField}
                              defaultValue={defaultValues.onSiteTime}
                              slotProps={{
                                textField: {
                                  variant: 'filled',
                                  className: classes.timePickerField,
                                  InputLabelProps: {
                                    shrink: true,
                                    className: classes.textFieldLabel,
                                  },
                                  InputProps: {
                                    disableUnderline: true,
                                    className: `${classes.textFieldInput} ${classes.timePickerInput}`,
                                  },
                                },
                                openPickerIcon: {
                                  className: classes.pickerIcon,
                                },
                              }}
                            />

                            <TimeInput
                              name="onBoardTime"
                              label={t('invoice.onBoard')}
                              control={control}
                              className={classes.timePickerField}
                              defaultValue={defaultValues.onBoardTime}
                              slotProps={{
                                textField: {
                                  variant: 'filled',
                                  className: classes.timePickerField,
                                  InputLabelProps: {
                                    shrink: true,
                                    className: classes.textFieldLabel,
                                  },
                                  InputProps: {
                                    disableUnderline: true,
                                    className: `${classes.textFieldInput} ${classes.timePickerInput}`,
                                  },
                                },
                                openPickerIcon: {
                                  className: classes.pickerIcon,
                                },
                              }}
                            />

                            <TimeInput
                              name="completedTime"
                              label={t('invoice.completed')}
                              control={control}
                              className={`${classes.timePickerField} ${
                                shouldHighlightCompletedTimeInput
                                  ? classes.highlightedTime
                                  : ''
                              }`}
                              defaultValue={defaultValues.completedTime}
                              slotProps={{
                                textField: {
                                  variant: 'filled',
                                  className: `${classes.timePickerField} ${
                                    shouldHighlightCompletedTimeInput
                                      ? classes.highlightedTime
                                      : ''
                                  }`,
                                  InputLabelProps: {
                                    shrink: true,
                                    className: `${classes.textFieldLabel} ${
                                      shouldHighlightCompletedTimeInput
                                        ? classes.highlightedLabel
                                        : ''
                                    }`,
                                  },
                                  InputProps: {
                                    disableUnderline: true,
                                    className: `${classes.textFieldInput} ${classes.timePickerInput}`,
                                  },
                                },
                                openPickerIcon: {
                                  className: classes.pickerIcon,
                                },
                              }}
                            />
                          </Box>
                        </LocalizationProvider>
                      )}
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Box>

            <Box className={classes.buttonSet}>
              <Button
                className={classes.nextButton}
                onClick={handleSubmit(handleNext)}
                variant="contained">
                {t('invoice.next')}
              </Button>
              <Button
                className={classes.closeButtonAction}
                onClick={() => handleClose(false)}
                variant="outlined">
                {t('invoice.close')}
              </Button>
            </Box>
          </Box>

          <Box className={classes.rightCard}>
            <Typography className={classes.summaryTitle}>
              {t('invoice.summary')}
            </Typography>
            <Box className={classes.summaryContent}>
              <Box>
                <Typography className={classes.summaryText}>
                  {t('invoice.id')}: <strong>{invoiceData.tripId}</strong>
                </Typography>
                <Typography className={classes.summaryText}>
                  {t('invoice.member')}:{' '}
                  <strong>{invoiceData.memberName}</strong>
                </Typography>
                <Typography className={classes.summaryText}>
                  {t('invoice.tripMode')}:{' '}
                  <strong>{invoiceData.tripMode}</strong>
                </Typography>
                <Typography className={classes.summaryText}>
                  {t('invoice.pickupTime')}:{' '}
                  <strong>{invoiceData.pickupTime}</strong>
                </Typography>
                <Typography className={classes.summaryText}>
                  {t('invoice.pickupLocation')}:{' '}
                  <strong>{invoiceData.pickupLocation}</strong>
                </Typography>
                <Typography className={classes.summaryText}>
                  {t('invoice.dropoffLocation')}:{' '}
                  <strong>{invoiceData.dropoffLocation}</strong>
                </Typography>
                <Typography className={classes.summaryText}>
                  {t('invoice.distance')}:{' '}
                  <strong>{invoiceData.distance}</strong>
                </Typography>
                <Typography
                  className={`${classes.summaryText} ${
                    highlightFlags.highlightPrice ? classes.highlighted : ''
                  }`}>
                  {t('invoice.price')}: <strong>{invoiceData.price}</strong>
                </Typography>
              </Box>
              <Box>
                <Typography
                  className={`${classes.summaryText} ${
                    highlightFlags.highlightProvider ? classes.highlighted : ''
                  }`}>
                  {t('invoice.provider')}:{' '}
                  <strong>{invoiceData.provider}</strong>
                </Typography>
                <Typography className={classes.summaryText}>
                  {t('invoice.driver')}:{' '}
                  <strong>{invoiceData.driverName}</strong>
                </Typography>
                {invoiceData.onSite && (
                  <Typography className={classes.summaryText}>
                    {t('invoice.onSite')}: <strong>{invoiceData.onSite}</strong>
                  </Typography>
                )}
                {invoiceData.onBoard && (
                  <Typography className={classes.summaryText}>
                    {t('invoice.onBoard')}:{' '}
                    <strong>{invoiceData.onBoard}</strong>
                  </Typography>
                )}
                {invoiceData.completed && (
                  <Typography
                    className={`${classes.summaryText} ${
                      highlightFlags.highlightCompletedTime
                        ? classes.highlighted
                        : ''
                    }`}>
                    {t('invoice.completed')}:{' '}
                    <strong>{invoiceData.completed}</strong>
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
}
